import React from 'react'
import Link from 'next/link'
import { useDispatch } from 'react-redux'
import { FiDisc } from '@react-icons/all-files/fi/FiDisc'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiRefreshCw } from '@react-icons/all-files/fi/FiRefreshCw'
import { FiSliders } from '@react-icons/all-files/fi/FiSliders'
import { SHOW_POPUP } from '../../../store/slices/ui'
import * as gtag from '../../../lib/gtag'
import { getDjComponentProducts, getDjSetupProducts } from '../../../utilities/djEquipmentCatalog'
import QuotePop from '../../DeferredQuotePopup'
import CatalogHelp from '../commerce/CatalogHelp'
import ProductCatalog from '../commerce/ProductCatalog'
import Container from '../foundations/Container'
import PageShell from '../foundations/PageShell'
import ClosingActionBand from './ClosingActionBand'
import EventShowcase from './EventShowcase'
import FAQGroup from './FAQGroup'
import MarketCoverageNotice from './MarketCoverageNotice'
import MediaFeature from './MediaFeature'
import RelatedServices from './RelatedServices'
import ReviewProof from './ReviewProof'
import ServiceCategoryGrid from './ServiceCategoryGrid'
import ServiceHero from './ServiceHero'
import ServiceIntroduction from './ServiceIntroduction'
import VisualDecisionGuide from './VisualDecisionGuide'
import djEquipmentHeroV2 from '../../../public/Pictures/categories-v2/dj-equipment-rental-hero-v2.jpg'
import styles from './DJEquipmentRentalPage.module.scss'

const PAGE_PATH = '/dj-equipment-rental'
const CANONICAL = `https://miamisoundrental.com${PAGE_PATH}`

const workflowIcons = {
  disc: FiDisc,
  refresh: FiRefreshCw,
  sliders: FiSliders
}

const asParagraphs = paragraphs => paragraphs?.map(paragraph => <p key={paragraph}>{paragraph}</p>)

const DJEquipmentRentalPage = ({
  content,
  newsletterevents = [],
  products = [],
  reviewData,
  totalEvents
}) => {
  const dispatch = useDispatch()
  const setupProducts = getDjSetupProducts(products)
  const componentProducts = getDjComponentProducts(products)

  const openQuote = sourceSurface => {
    gtag.event({
      action: 'quote_click',
      category: 'orders',
      label: 'leads',
      sourceSurface,
      value: 3
    })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const trackCall = sourceSurface => gtag.event({
    action: 'booking_assistance_call_CTA',
    category: 'orders',
    label: 'leads',
    sourceSurface,
    value: 2
  })

  const primaryHeroAction = {
    label: content.hero.primaryAction,
    href: '#dj-workflow-guide',
    icon: FiSliders,
    conversionKey: 'dj-equipment-rental.hero.choose-workflow',
    channel: 'rental',
    salesPath: 'both',
    focusTargetOnNavigate: true,
    onClick: () => gtag.event({
      action: 'dj_equipment_workflow_start',
      category: 'orders',
      label: 'leads',
      sourceSurface: 'hero',
      value: 2
    })
  }

  const compatibilityAction = {
    label: content.hero.secondaryAction,
    icon: FiMessageCircle,
    conversionKey: 'dj-equipment-rental.hero.talk-to-expert',
    channel: 'quote',
    salesPath: 'managed',
    onClick: () => openQuote('hero')
  }

  const callAction = {
    label: content.hero.tertiaryAction,
    href: 'tel:+13059744411',
    icon: FiPhone,
    conversionKey: 'dj-equipment-rental.hero.call',
    channel: 'phone',
    salesPath: 'managed',
    onClick: () => trackCall('hero')
  }

  const workflowItems = content.workflow.items.map(item => ({
    ...item,
    icon: workflowIcons[item.icon] || FiDisc,
    conversionChannel: 'other',
    conversionLocation: 'dj-workflow-guide',
    conversionSalesPath: 'self_service',
    onClick: () => gtag.event({
      action: 'dj_equipment_workflow_select',
      category: 'orders',
      destination: item.href,
      label: item.key,
      sourceSurface: 'dj-workflow-guide',
      value: 1
    })
  }))

  const specialistServices = content.specialists.items.map(item => ({
    h1: item.title,
    url: item.href,
    miniText: item.description,
    conversionChannel: 'other',
    conversionKey: item.conversionKey,
    conversionLocation: 'dj-specialist-categories',
    conversionSalesPath: 'self_service',
    onClick: () => gtag.event({
      action: 'dj_equipment_specialist_select',
      category: 'orders',
      destination: item.href,
      label: item.title,
      sourceSurface: 'dj-specialist-categories',
      value: 1
    })
  }))

  const relatedServices = content.related.items.map(item => ({
    h1: item.title,
    url: item.href,
    miniText: item.description
  }))

  const closingSetupAction = {
    label: content.finalCta.primaryAction,
    href: '#dj-setup-catalog',
    icon: FiDisc,
    conversionKey: 'dj-equipment-rental.closing.browse-setups',
    channel: 'rental',
    salesPath: 'self_service',
    focusTargetOnNavigate: true,
    onClick: () => gtag.event({
      action: 'catalog_click',
      category: 'orders',
      label: 'leads',
      sourceSurface: 'closing-action',
      value: 2
    })
  }

  const closingCompatibilityAction = {
    ...compatibilityAction,
    label: content.finalCta.secondaryAction,
    closingConversionKey: 'dj-equipment-rental.closing.compatibility-help',
    onClick: () => openQuote('closing-action')
  }

  return (
    <PageShell>
      <ServiceHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'DJ Equipment Rental' }
        ]}
        eyebrow={content.hero.eyebrow}
        title={content.hero.title}
        description={content.hero.description}
        image={{ src: djEquipmentHeroV2, position: content.hero.imagePosition || '65% center' }}
        imageAlt={content.hero.imageAlt}
        primaryAction={primaryHeroAction}
        secondaryAction={compatibilityAction}
        tertiaryAction={callAction}
        processLabel={content.process.label}
        steps={content.process.steps}
        stackActionsOnMobile
      />

      <MarketCoverageNotice content={content.marketNotice} />

      <VisualDecisionGuide
        id='dj-workflow-guide'
        eyebrow={content.workflow.eyebrow}
        heading={content.workflow.title}
        description={content.workflow.description}
        items={workflowItems}
      />

      <ServiceIntroduction
        eyebrow={content.introduction.eyebrow}
        heading={content.introduction.title}
        headingId='dj-equipment-service-introduction'
        body={asParagraphs(content.introduction.paragraphs)}
        guideHeading={content.introduction.guideHeading}
        guideHeadingId='dj-equipment-selection-guide'
        guideSummary={content.introduction.guideSummary}
        guideBody={asParagraphs(content.introduction.guideParagraphs)}
        image={content.introduction.image}
        imageAlt={content.introduction.imageAlt}
        signals={content.introduction.signals}
      />

      <ProductCatalog
        carousel={{
          name: 'Complete DJ Setup Rentals',
          url: CANONICAL,
          description: 'Configured DJ equipment setups available to rent in Miami and South Florida'
        }}
        filters={[
          ['CDJ Setups', 'cdj'],
          ['Controller Setups', 'controller'],
          ['Turntable Setups', 'turntable'],
          ['DJ Booth Setups', 'booth']
        ]}
        sortOptions={['DJ Level', 'Release Year']}
        listName='Complete DJ Setup Rentals'
        anchorId='dj-setup-catalog'
        eyebrow={content.setups.eyebrow}
        description={content.setups.description}
        products={setupProducts}
        inventoryMarket='Miami'
      />

      <Container className={styles.catalogHelp}>
        <CatalogHelp
          ariaLabel='DJ setup compatibility assistance'
          heading={content.setups.helpHeading}
          description={content.setups.helpDescription}
          quoteLabel={content.setups.helpAction}
          onQuote={() => openQuote('complete-dj-setup-catalog')}
          onCall={() => trackCall('complete-dj-setup-catalog')}
        />
      </Container>

      <RelatedServices
        eyebrow={content.specialists.eyebrow}
        heading={content.specialists.title}
        description={content.specialists.description}
        headingId='dj-specialist-categories'
        additionalServices={specialistServices}
      />

      <ProductCatalog
        carousel={{
          name: 'Individual DJ Equipment Rentals',
          url: CANONICAL,
          description: 'Individual CDJs, mixers, controllers, turntables, DJ booths, and effects available to rent in Miami'
        }}
        filters={[
          ['CDJs & Media Players', 'cdj'],
          ['DJ Mixers', 'mixer'],
          ['DJ Controllers', 'controller'],
          ['Turntables', 'turntable'],
          ['DJ Booths', 'booth'],
          ['DJ Effects', 'effects']
        ]}
        sortOptions={['Release Year', 'DJ Level']}
        listName='Individual DJ Equipment Rentals'
        anchorId='dj-component-catalog'
        eyebrow={content.components.eyebrow}
        description={content.components.description}
        products={componentProducts}
        inventoryMarket='Miami'
      />

      <MediaFeature
        eyebrow={content.boundary.eyebrow}
        heading={content.boundary.title}
        body={asParagraphs(content.boundary.paragraphs)}
        secondaryHeading={content.boundary.secondaryHeading}
        secondaryBody={(
          <p>
            {content.boundary.secondaryParagraph}{' '}
            <Link href='/speaker-rental'>Compare speakers</Link> or{' '}
            <Link href='/sound-system-rental'>plan a complete sound system</Link>.
          </p>
        )}
        image={content.boundary.image}
        imageAlt={content.boundary.imageAlt}
        imagePosition='center 58%'
        reverse
      />

      <RelatedServices
        eyebrow={content.related.eyebrow}
        heading={content.related.title}
        description={content.related.description}
        headingId='dj-related-event-systems'
        additionalServices={relatedServices}
      />

      <EventShowcase
        eyebrow={content.events.eyebrow}
        heading={content.events.title}
        description={content.events.description}
        filter={['party', 'concert', 'private', 'wedding', 'corporate']}
        events={newsletterevents}
        totalEvents={totalEvents}
        sectionId='dj-equipment-production-proof'
      />

      <ReviewProof
        filter={['party', 'convenient', 'sound', 'dj']}
        eyebrow={content.reviews.eyebrow}
        heading={content.reviews.title}
        description={content.reviews.description}
        initialData={reviewData}
        pagePath={PAGE_PATH}
        sectionId='dj-equipment-client-reviews'
      />

      <FAQGroup
        questions={content.faqs}
        eyebrow='DJ equipment rental answers'
        title='DJ Equipment Rental Questions'
        headingId='dj-equipment-rental-faq-title'
        intro='Answers about complete setups, standalone components, technical riders, inclusions, audience sound, pickup, delivery, setup, and service areas.'
      />

      <ServiceCategoryGrid
        currentCategory='dj_equipment'
        description='Continue to the proper family for sound, visuals, staging, lighting, or special effects without changing the job of this DJ-equipment gateway.'
      />

      <ClosingActionBand
        ariaLabel='DJ equipment rental options'
        eyebrow={content.finalCta.eyebrow}
        heading={content.finalCta.title}
        headingId='dj-equipment-closing-action-heading'
        description={content.finalCta.description}
        proofItems={content.finalCta.proofItems}
        primaryAction={closingSetupAction}
        secondaryAction={closingCompatibilityAction}
      />
    </PageShell>
  )
}

export default DJEquipmentRentalPage
